import { z } from 'zod'
import type { CertFacts } from './facts'
import { certRule } from './rules'
import type { CertTarget } from './targets'

/**
 * AI 심사에 얹는 인증 판정 (설계 3-6, 2026-11-01). 따로 AI 를 부르지 않고 기존 심사(gate.ts judge) 한 번에 같이 받는다.
 * 코드가 못 보는 뜻만 묻는다 (발효 알코올의 성격, 복합원료 속 숨은 원료, 메모 속 인증 단정, 식품유형 판정).
 * 공장·서류·심사로만 알 수 있는 것은 묻지 않는다. 판정은 realism·consistency·safety 점수에 넣지 않는다.
 *
 * ok 는 '설계 조건을 지켰다' 는 뜻일 뿐이다. AI 의 ok 는 ✅ 가 되지 않고(코드 검사만 ✅), AI 가 찾은 fix 만 ❌ 로 쓴다 (readiness.ts).
 * why 는 관리자 화면(internal.certEvidence)에서만 본다. 앱에는 고정 문구만 나간다.
 */
export interface CertJudgeItem {
  /** 심사 출력의 id (H-HIDDEN 은 규칙 id 가 아니라 '숨은 원료' 질문이다) */
  id: string
  /** 판정이 붙는 규칙 */
  ruleId: string
  target: CertTarget
  /** '있을 때만 fix' 로 묻는 항목 (빠진 것만 찾는 질문). AI 의 ok 는 어느 항목이든 ✅ 가 되지 않는다 */
  fixOnly?: boolean
}

export const CERT_JUDGE_ITEMS: readonly CertJudgeItem[] = [
  { id: 'H-ALC-03', ruleId: 'H-ALC-03', target: 'halal' },
  { id: 'H-HIDDEN', ruleId: 'H-SRC-07', target: 'halal', fixOnly: true },
  { id: 'H-LBL-01', ruleId: 'H-LBL-01', target: 'halal' },
  { id: 'H-LBL-02', ruleId: 'H-LBL-02', target: 'halal' },
  { id: 'H-ING-05', ruleId: 'H-ING-05', target: 'halal', fixOnly: true },
  { id: 'HK-SCOPE-01', ruleId: 'HK-SCOPE-01', target: 'haccp' },
  { id: 'HK-PLAN-03', ruleId: 'HK-PLAN-03', target: 'haccp', fixOnly: true },
  { id: 'HK-PLAN-04', ruleId: 'HK-PLAN-04', target: 'haccp', fixOnly: true },
  { id: 'HK-PRP-18', ruleId: 'HK-PRP-18', target: 'haccp', fixOnly: true },
]

/** 이 제품에서 심사에 물을 항목 (고른 목표 + 규칙의 해당 조건). 없으면 [] — 심사 프롬프트가 지금과 같다 */
export function certJudgeItemsFor(facts: CertFacts): CertJudgeItem[] {
  return CERT_JUDGE_ITEMS.filter((item) => {
    if (!facts.goals.targets.includes(item.target)) return false
    const rule = certRule(item.ruleId)
    return !rule?.when || rule.when(facts)
  })
}

export const certJudgeVerdictSchema = z.object({
  id: z.string().max(20),
  v: z.enum(['ok', 'fix', 'unknown']),
  /** 재료 id 또는 몇 번째 step (1부터) */
  ref: z.string().max(60).optional(),
  why: z.string().max(200).optional(),
})
export type CertJudgeVerdict = z.infer<typeof certJudgeVerdictSchema>

/** 심사 출력의 "cert" 를 느슨하게 읽는다. 모르는 id·틀린 모양은 버린다 (판정이 없으면 그 항목은 ⚠️ 로 남는다) */
export function parseCertJudgeVerdicts(
  raw: unknown,
  asked: readonly CertJudgeItem[],
): CertJudgeVerdict[] {
  if (!Array.isArray(raw)) return []
  const ids = new Set(asked.map((a) => a.id))
  const out: CertJudgeVerdict[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const e = entry as Record<string, unknown>
    const ref = e['ref']
    const parsed = certJudgeVerdictSchema.safeParse({
      id: e['id'],
      v: typeof e['v'] === 'string' ? e['v'].toLowerCase() : e['v'],
      ...(typeof ref === 'string' || typeof ref === 'number'
        ? { ref: String(ref).slice(0, 60) }
        : {}),
      ...(typeof e['why'] === 'string' ? { why: e['why'].slice(0, 200) } : {}),
    })
    if (parsed.success && ids.has(parsed.data.id) && !out.some((o) => o.id === parsed.data.id))
      out.push(parsed.data)
  }
  return out
}
